import { createContext, useContext } from 'react'

const ViewportWidthContext = createContext<number | null>(null)

type ViewportWidthProviderProps = {
  /**
   * The width, in CSS pixels, that the subtree lays out at. `null` hands the
   * subtree back to the browser window.
   */
  width: number | null
  children: React.ReactNode
}

/**
 * Lets a subtree lay out as if the viewport were a given width, for a product
 * mockup or a device preview drawn inside a page.
 *
 * Popcorn Kernel uses it: the marketing site renders the product's screens
 * from Popcorn components inside a frame on its pages, at the width of the
 * page's column rather than the browser window's. Each frame provides the
 * width its screen lays out at, so the Popcorn components in it switch mode
 * as they would in the product at that width.
 *
 * The width media queries that `useMediaQuery` and `useBreakpoint` evaluate
 * read this width instead of the window's, so a component that switches mode
 * by screen size (a sidebar to a drawer, a menu to a sheet) follows the box it
 * sits in. Queries on anything else, such as the colour scheme or reduced
 * motion, still go to the window.
 *
 * CSS media queries cannot see this width. A subtree that also needs CSS to
 * follow it has to answer container queries instead.
 *
 * @see services/marketing/src/components/product/ProductFrame/ProductFrame.tsx
 */
export function ViewportWidthProvider({
  width,
  children,
}: ViewportWidthProviderProps): JSX.Element {
  return (
    <ViewportWidthContext.Provider value={width}>
      {children}
    </ViewportWidthContext.Provider>
  )
}

/**
 * The width set by the nearest `ViewportWidthProvider`, or `null` when the
 * subtree follows the browser window.
 */
export function useViewportWidth(): number | null {
  return useContext(ViewportWidthContext)
}

const WIDTH_FEATURE =
  /^\(\s*(?:(min-width|max-width)\s*:\s*(\d+(?:\.\d+)?)px|width\s*(<=|>=|<|>|=)\s*(\d+(?:\.\d+)?)px)\s*\)$/

function matchesWidthFeature(feature: string, width: number): boolean | null {
  const match = WIDTH_FEATURE.exec(feature.trim())
  if (!match) return null

  const [, prefixed, prefixedValue, operator, rangeValue] = match
  if (prefixed === 'min-width') return width >= Number(prefixedValue)
  if (prefixed === 'max-width') return width <= Number(prefixedValue)

  const value = Number(rangeValue)
  switch (operator) {
    case '<':
      return width < value
    case '<=':
      return width <= value
    case '>':
      return width > value
    case '>=':
      return width >= value
    default:
      return width === value
  }
}

/**
 * Evaluates a media query against `width` when the query tests nothing but
 * width, in pixels: `(width < 768px)`, `(min-width: 640px)`, or several of
 * those joined with `and`. Returns `null` for any other query, which the
 * caller hands to the browser instead.
 */
export function matchWidthQuery(query: string, width: number): boolean | null {
  const features = query.split(/\s+and\s+/)
  let matches = true
  for (const feature of features) {
    const result = matchesWidthFeature(feature, width)
    if (result === null) return null
    matches = matches && result
  }
  return matches
}
